import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, ReplaySubject } from 'rxjs';

import { environment } from 'environments/environment';

import { AuthUtils } from 'app/core/auth/auth.utils';
import { PreferenceCalendarFilterType, UserType } from 'app/core/user/user.types';
import { Common } from 'app/modules/_common/service';

@Injectable({providedIn: 'root'})
export class UserService
{
    private _httpClient = inject(HttpClient);
    private _user: ReplaySubject<UserType> = new ReplaySubject<UserType>(1);

    constructor()
    {
    }

    get BASEURL(): string
    {
        return environment.api.replace("[hostinglocation]", AuthUtils.hostingLocation) + '/api/v2/';
    }

    get user$(): Observable<UserType>
    {
        return this._user.asObservable();
    }

    _userData: UserType = null;
    get user(): UserType
    {
        if (this._userData != null) {
            return this._userData;
        }

        try {
            var ud = localStorage.getItem('_ud');
            if (!ud || ud === undefined || ud == null) {
                return null;
            }

            this._userData = JSON.parse(ud, (prop, value) => { const lower = prop.charAt(0).toLowerCase() + prop.slice(1); return prop === lower ? value : this[lower] = value;});

            return this._userData;

        } catch(e) {
            console.error(e);

            this._userData = null;

            return null;
        }
    }

    fetchProfileImage(nocache?: boolean): Observable<string>
    {
        const personId = this.user?.personId;
        if (!personId) {
            return of(null);
        }
        if (!nocache && this.user?._profileImage != null) {
            return of(this.user._profileImage);
        }

        return this._httpClient.get<string>(`${this.BASEURL}person/${personId}/filebase64`).pipe(
            map((base64) =>
            {
                return base64 ? `data:image/png;base64, ${base64}` : null;
            }),
            catchError(Common.CatchError)
        );
    }

    updateCacheField(field, value): UserType
    {
        try {
            var person: UserType = this.user;

            person['_personImage'] = null;
            person[field] = value;

            localStorage.setItem('_ud', JSON.stringify(person));

            this._user.next(this.user);

        } catch (ex) { }

        return this.user;
    }

    isRefreshing: boolean = false;
    refreshCache(nocache?: boolean)
    {
        if (this.isRefreshing) {
            return;
        }

        this.isRefreshing = true;

        this.fetchProfileImage(nocache).pipe(
            map(image => {
                this._userData = null;

                this.user._profileImage = image;

                this._user.next(this.user);

                return !!image;
            })
        ).subscribe(()=> this.isRefreshing = false);
    }

    public static set preference_includeWeekends(value: boolean)
    {
        localStorage.setItem('preference_includeWeekends', value.toString());
    }
    public static get preference_includeWeekends(): boolean
    {
        return localStorage.getItem('preference_includeWeekends') == "true" ? true : false;
    }

    public static set preference_calendar_filter(value: PreferenceCalendarFilterType[])
    {
        localStorage.setItem('preference_calendar_filter', JSON.stringify(value));
    }
    public static get preference_calendar_filter(): PreferenceCalendarFilterType[]
    {
        try {
            const stored = localStorage.getItem('preference_calendar_filter');
            if (!stored) {
                // Initialize with default values for backward compatibility
                return [
                    { name: 'excludeDaysWithNoBookings', value: 'excludeDaysWithNoBookings', isSelected: false },
                    { name: 'isOfficeDays', value: 'isOfficeDays', isSelected: false },
                    { name: 'no-bookings', value: 'no-bookings', isSelected: false }
                ];
            }

            const list: PreferenceCalendarFilterType[] = JSON.parse(stored);
            if (list.length  < 3) {
                throw new Error('updating preference calendar filter');
            }

            return list;
        } catch (e) {
            console.error('Error parsing preference_calendar_filter:', e);
            return [
                { name: 'excludeDaysWithNoBookings', value: 'excludeDaysWithNoBookings', isSelected: false },
                { name: 'isOfficeDays', value: 'isOfficeDays', isSelected: false },
                { name: 'no-bookings', value: 'no-bookings', isSelected: false }
            ];
        }
    }

    public static set preference_displayStyle(value: string)
    {
        localStorage.setItem('preference_displayStyle', value);
    }
    public static get preference_displayStyle(): string
    {
        const stored = localStorage.getItem('preference_displayStyle');
        return stored || 'firstLetter'; // Default to 'firstLetter' if not set
    }

}
